"use client";

import { useSignals } from "@preact/signals-react/runtime";
import { useId } from "react";

import {
  setDialog,
  useDialogState,
  useRegisterDialogBeforeClose,
} from "@/components/app/dialogs/dialog-provider";
import type {
  DialogProps,
  RoutedDialogName,
} from "@/components/app/dialogs/dialog-registry";
import type { RoutedDialogBeforeCloseHandler } from "@/components/app/dialogs/dialog-types";

type LocalEntryDialogInstanceProps = {
  instanceId?: string;
};

export const useLocalEntryDialog = <Name extends RoutedDialogName>(
  dialog: Name,
  options?: {
    beforeClose?: RoutedDialogBeforeCloseHandler | null;
    props?: DialogProps<Name>;
  },
) => {
  useSignals();

  const instanceId = useId();
  const dialogState = useDialogState(dialog);
  const dialogProps = dialogState?.props as
    | (DialogProps<Name> & LocalEntryDialogInstanceProps)
    | undefined;
  const open = Boolean(
    dialogState?.open && dialogProps?.instanceId === instanceId,
  );

  useRegisterDialogBeforeClose(
    dialog,
    open ? (options?.beforeClose ?? null) : null,
  );

  const setOpen = (nextOpen: boolean) => {
    if (!nextOpen) {
      setDialog(dialog, { open: false });
      return;
    }

    setDialog(dialog, {
      open: true,
      historyMode: "entry",
      props: {
        ...options?.props,
        instanceId,
      } as unknown as DialogProps<Name>,
    });
  };

  return {
    instanceId,
    open,
    props: dialogProps,
    setOpen,
  };
};
